import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Loader2, X } from 'lucide-react';
import { CurrencyIcon } from '@/components/common/CurrencyIcon';
import { cashMovementApi } from '@/services/api';
import { getCurrencySymbol } from '@/utils/currency';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import toast from 'react-hot-toast';

type MovementType = 'Paid In' | 'Paid Out';

interface Props {
  open: boolean;
  onClose: () => void;
  movementType: MovementType;
  shiftName?: string;
  cashPocket?: string;
  pocketName?: string;
  onSuccess?: () => void;
}

const THEME: Record<
  MovementType,
  {
    headerBg: string;
    headerBorder: string;
    title: string;
    subtitle: string;
    icon: typeof ArrowDownLeft;
    iconWrap: string;
    submitBtn: string;
  }
> = {
  'Paid In': {
    headerBg: 'bg-emerald-50',
    headerBorder: 'border-emerald-100',
    title: 'text-emerald-950',
    subtitle: 'text-emerald-700/80',
    icon: ArrowDownLeft,
    iconWrap: 'bg-emerald-100 text-emerald-700',
    submitBtn: 'bg-emerald-600 hover:bg-emerald-700 focus-visible:ring-emerald-500',
  },
  'Paid Out': {
    headerBg: 'bg-orange-50',
    headerBorder: 'border-orange-100',
    title: 'text-orange-950',
    subtitle: 'text-orange-700/80',
    icon: ArrowUpRight,
    iconWrap: 'bg-orange-100 text-orange-700',
    submitBtn: 'bg-orange-600 hover:bg-orange-700 focus-visible:ring-orange-500',
  },
};

function emptyForm() {
  return { amount: '', reason: '', remarks: '', error: '' };
}

export default function CashMovementModal({
  open,
  onClose,
  movementType,
  shiftName,
  cashPocket,
  pocketName,
  onSuccess,
}: Props) {
  useEscapeToClose(onClose, open);
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isPaidIn = movementType === 'Paid In';
  const theme = THEME[movementType];
  const Icon = theme.icon;

  useEffect(() => {
    if (!open) {
      const cleared = emptyForm();
      setAmount(cleared.amount);
      setReason(cleared.reason);
      setRemarks(cleared.remarks);
      setError(cleared.error);
      setSubmitting(false);
    }
  }, [open, movementType]);

  if (!open) return null;

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt <= 0) {
      setError(t('cashMovementModal.amountRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await cashMovementApi.create({
        movement_type: movementType,
        amount: amt,
        reason: reason || movementType,
        remarks,
        pos_shift: shiftName,
        cash_pocket: cashPocket,
      });
      toast.success(t(isPaidIn ? 'cashMovementModal.savedIn' : 'cashMovementModal.savedOut'));
      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        t('cashMovementModal.failed');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/55 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
        <div
          className={`flex items-start justify-between gap-3 px-6 py-5 border-b shrink-0 ${theme.headerBg} ${theme.headerBorder}`}
        >
          <div className="flex items-start gap-3 min-w-0">
            <div className={`p-2.5 rounded-xl shrink-0 ${theme.iconWrap}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className={`font-semibold text-lg ${theme.title}`}>
                {t(isPaidIn ? 'cashMovementModal.paidInTitle' : 'cashMovementModal.paidOutTitle')}
              </h3>
              <p className={`text-xs mt-0.5 ${theme.subtitle}`}>
                {t(isPaidIn ? 'cashMovementModal.paidInSubtitle' : 'cashMovementModal.paidOutSubtitle')}
              </p>
              {pocketName && (
                <p className="text-xs text-gray-500 mt-1 truncate">
                  {t('cashMovementModal.drawer', { name: pocketName })}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.amount')} ({getCurrencySymbol()})
              </label>
              <div className="relative">
                <CurrencyIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="input-field pl-9 font-mono"
                  placeholder="0.00"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('cashMovementModal.reason')}
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="input-field"
                placeholder={t(
                  isPaidIn ? 'cashMovementModal.reasonInPlaceholder' : 'cashMovementModal.reasonOutPlaceholder',
                )}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('cashMovementModal.remarks')}
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="input-field min-h-[88px]"
                placeholder={t('cashMovementModal.remarksPlaceholder')}
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 p-3.5 rounded-xl text-sm flex items-start gap-2 border border-red-100">
                <AlertTriangle className="w-5 h-5 shrink-0 text-red-500" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 py-2.5 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 ${theme.submitBtn}`}
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {t(isPaidIn ? 'cashMovementModal.saveIn' : 'cashMovementModal.saveOut')}
            </button>
            <button type="button" onClick={handleClose} className="btn-secondary py-2.5">
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
